import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  Validators
} from '@angular/forms';
import {
  DateBoxTypesEnum,
  Datetime
} from 'app/charts-dashboard/interfaces';
import {
  dateFormat,
  disableUnsuitableDates,
  momentDateTimeFormat,
  realtimeWorkingDays,
  timeFormat
} from '../../../helpers/charts-module-helpers';
import {
  distinctUntilChanged,
  takeUntil
} from 'rxjs/operators';
import { Destroyer } from 'app/common/utils';
import { Subscription } from 'rxjs/Subscription';
import * as moment from 'moment';

interface IDateTimeControl<T> {
  date: T;
  time: T;
}

@Component({
  selector: 'iv-live-date-time-selector',
  templateUrl: './date-time-selector.component.html',
  styleUrls: ['./date-time-selector.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => DateTimeSelectorComponent),
    multi: true
  }]
})
export class DateTimeSelectorComponent extends Destroyer implements OnInit, OnDestroy {

  constructor(
  ) {
    super();
  }

  public get disabled(): boolean {
    return this._disabled;
  }

  public set disabled(value: boolean) {
    this._disabled = value;
  }

  @Input()
  public locale: IDateTimeControl<string>;

  @Input()
  public isActionControl: boolean;

  @Input()
  public clearButton: IDateTimeControl<boolean>;

  @Input()
  public container: Element;

  @Output()
  public isEod = new EventEmitter<boolean>();

  public dateTimeControl = new FormGroup({
    date: new FormControl(null, Validators.required)
  });
  public dateBoxTypes = DateBoxTypesEnum;
  public dateFormat = dateFormat;
  public timeFormat = timeFormat;
  public disableUnsuitableDates = disableUnsuitableDates;

  private _disabled = false;
  private controlSubscription: Subscription;

  private static mapSelectedDateTime(dateTime: IDateTimeControl<string>): string {
    const date: string = `${dateTime.date} ${dateTime.time ? dateTime.time : '23:59:59'}`;

    return moment(new Date(date), 'MM-DD-YYYY HH:mm:ss A').format(momentDateTimeFormat);
  }

  public ngOnInit(): void {
    this.controlSubscription = this.dateTimeSubscription();

    if (this.isActionControl) {
      this.dateTimeControl.get(DateBoxTypesEnum.DATE).clearValidators();
    }
  }

  public ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  public opened(event: any): void {
    if (!this.container) return;

    event.component._popup.option('container', this.container);
  }

  private dateTimeSubscription(): Subscription {
    return this.dateTimeControl
      .valueChanges
      .pipe(
        distinctUntilChanged((prev: IDateTimeControl<string>, cur: IDateTimeControl<string>) => {
          if (cur.time && !prev.time && prev.date && cur.date) return false;
          if (cur.time && prev.time) {
            return prev.date === cur.date && prev.time.slice(0, 5) === cur.time.slice(0, 5);
          }
          return prev.date === cur.date;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((dateTime: IDateTimeControl<string>) => {
        if (!dateTime.date) {
          this.dateTimeControl.removeControl(DateBoxTypesEnum.TIME);
          this.isEod.emit(false);
          return;
        }

        const selectedDate = DateTimeSelectorComponent.mapSelectedDateTime(dateTime);
        const currentTimeStamp = moment(selectedDate).unix() * 1000;

        if (currentTimeStamp < realtimeWorkingDays) {
          this.dateTimeControl.removeControl(DateBoxTypesEnum.TIME);
        } else {
          if (!this.dateTimeControl.get(DateBoxTypesEnum.TIME)) {
            this.dateTimeControl.addControl(DateBoxTypesEnum.TIME, new FormControl());
          }
        }

        this.onChange(selectedDate);
        this.onTouched();
        this.isEod.emit(currentTimeStamp < realtimeWorkingDays);
      });
  }

  // ----------- Методы ControlValueAccessor -----------
  // Вызовет форма, если значение изменилось извне
  public writeValue(outsideValue: Datetime): void {
    if (!outsideValue) return;

    const date = moment(new Date(outsideValue), 'MM-DD-YYYY HH:mm:ss A').format(momentDateTimeFormat);
    const currentTimeStamp = moment(date).unix() * 1000;

    this.dateTimeControl.get(DateBoxTypesEnum.DATE).setValue(date.slice(0, 10), {emitEvent: false});

    if (currentTimeStamp > realtimeWorkingDays) {
      if (!this.dateTimeControl.get(DateBoxTypesEnum.TIME)) {
        this.dateTimeControl.addControl(DateBoxTypesEnum.TIME, new FormControl(date.slice(11, 19)));
      } else {
        this.dateTimeControl.get(DateBoxTypesEnum.TIME).setValue(date.slice(11, 19), {emitEvent: false});
      }
    }
  }

  // Сохраняем обратный вызов для изменений
  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  // Сохраняем обратный вызов для "касаний"
  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  // Вызовем когда значение изменится
  private onChange(_: any): void {
  }

  // Вызовем при любом действии пользователя с контроллом
  private onTouched(): void {
  }

  // Установка состояния disabled
  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;

    switch (isDisabled) {
      case true:
        this.controlSubscription.unsubscribe();
        this.dateTimeControl.disable();
        break;
      case false:
        this.controlSubscription = this.dateTimeSubscription();
        this.dateTimeControl.enable();
        break;
    }
  }
}
